import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRegister, useGoogleLogin } from '@/hooks/useApi';
import { useAuthStore } from '@/stores/authStore';
import GoogleSignInButton from '@/components/auth/GoogleSignInButton';
import toast from 'react-hot-toast';
import api from '@/lib/api';
import type { AxiosError } from 'axios';

interface FieldErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  firstName?: string;
  lastName?: string;
}

const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).+$/;

// Map backend validation keys to frontend translation keys
const BACKEND_ERROR_MAP: Record<string, { field: keyof FieldErrors; key: string }> = {
  'auth.password.complexity': { field: 'password', key: 'auth.passwordComplexity' },
  'auth.password.min_length': { field: 'password', key: 'auth.passwordMinLength' },
  'auth.password.required': { field: 'password', key: 'auth.passwordMinLength' },
  'auth.email.required': { field: 'email', key: 'auth.emailRequired' },
  'auth.email.invalid': { field: 'email', key: 'auth.emailInvalid' },
  'auth.first_name.required': { field: 'firstName', key: 'auth.firstNameRequired' },
  'auth.last_name.required': { field: 'lastName', key: 'auth.lastNameRequired' },
};

export default function RegisterPage() {
  const { t } = useTranslation();
  const register = useRegister();
  const googleLogin = useGoogleLogin();
  const setAuth = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation');
  const [googleError, setGoogleError] = useState('');
  const [invitation, setInvitation] = useState<{ email: string; organization: { name: string }; role: string } | null>(null);
  const [invitationError, setInvitationError] = useState('');

  const handleGoogleCredential = async (idToken: string) => {
    setGoogleError('');
    try {
      const result = await googleLogin.mutateAsync({ idToken });
      setAuth(result.token, result.refreshToken, result.user);
      if (result.organization?.id) {
        useAuthStore.getState().setOrganizationId(result.organization.id);
      }
      toast.success(t('auth.registerSuccess'));
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string; error?: string }>;
      const status = axiosErr?.response?.status;
      const errorCode = axiosErr?.response?.data?.error;
      if (status === 403 && errorCode === 'google_email_not_verified') {
        setGoogleError(t('auth.googleEmailNotVerified'));
      } else {
        setGoogleError(axiosErr?.response?.data?.message || t('auth.googleSignInFailed'));
      }
    }
  };

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: '',
    // Honeypot: hidden from real users, bots that fill it get rejected server-side.
    website: '',
  });

  // If we arrived with ?invitation=TOKEN, look it up and prefill the email.
  useEffect(() => {
    if (!invitationToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invitations/${invitationToken}`);
        if (cancelled) return;
        setInvitation(data.data);
        setForm((f) => ({ ...f, email: data.data.email }));
      } catch {
        if (!cancelled) setInvitationError(t('auth.invitationInvalid'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invitationToken, t]);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const updateField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field as keyof FieldErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FieldErrors = {};

    if (!form.firstName.trim()) {
      newErrors.firstName = t('auth.firstNameRequired');
    }
    if (!form.lastName.trim()) {
      newErrors.lastName = t('auth.lastNameRequired');
    }
    if (!form.email.trim()) {
      newErrors.email = t('auth.emailRequired');
    }
    if (form.password.length < 8) {
      newErrors.password = t('auth.passwordMinLength');
    } else if (!PASSWORD_REGEX.test(form.password)) {
      newErrors.password = t('auth.passwordComplexity');
    }
    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordsDoNotMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const result = await register.mutateAsync({ ...form, invitationToken: invitationToken ?? '' });
      toast.success(t('auth.registerSuccess'));
      navigate('/confirm-account?email=' + encodeURIComponent(result.email));
    } catch (err: unknown) {
      const axiosErr = err as AxiosError<{ error?: string; errors?: Record<string, string> }>;

      // Try to parse backend field validation errors (422)
      const backendErrors = axiosErr?.response?.data?.errors;
      if (backendErrors && typeof backendErrors === 'object') {
        const mapped: FieldErrors = {};
        for (const value of Object.values(backendErrors)) {
          const mapping = BACKEND_ERROR_MAP[value];
          if (mapping) {
            mapped[mapping.field] = t(mapping.key);
          }
        }
        if (Object.keys(mapped).length > 0) {
          setErrors(mapped);
          return;
        }
      }

      // Duplicate account (409)
      if (axiosErr?.response?.status === 409) {
        setErrors({ email: t('auth.emailAlreadyExists') });
        toast.error(t('auth.emailAlreadyExists'));
        return;
      }

      // Too many attempts (429)
      if (axiosErr?.response?.status === 429) {
        toast.error(axiosErr.response.data?.error || t('auth.registrationFailed'));
        return;
      }

      toast.error(axiosErr?.response?.data?.error || t('auth.registrationFailed'));
    }
  };

  const inputClass = (field: keyof FieldErrors) =>
    `input ${errors[field] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`;

  return (
    <div>
      <div className="mb-8">
        <div className="lg:hidden flex items-center gap-2 mb-6">
          <img src="/logo.svg" alt="Ledgora" className="w-10 h-10" />
          <span className="text-xl font-bold text-gray-900">{import.meta.env.VITE_APP_NAME}</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.register')}</h2>
        <p className="mt-2 text-sm text-gray-600">
          {t('auth.hasAccount')}{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
            {t('auth.login')}
          </Link>
        </p>
      </div>

      {googleError && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {googleError}
        </div>
      )}

      {invitation && (
        <div className="mb-4 rounded-lg bg-primary-50 border border-primary-200 px-4 py-3 text-sm text-primary-800">
          {t('auth.invitationBanner', { org: invitation.organization.name })}
        </div>
      )}
      {invitationError && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          {invitationError}
        </div>
      )}

      <div className="mb-5 relative">
        <GoogleSignInButton
          onCredential={handleGoogleCredential}
          text="signup_with"
          disabled={googleLogin.isPending || register.isPending}
        />
        {googleLogin.isPending && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-white/80 text-sm font-medium text-gray-600">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            {t('auth.signingIn')}
          </div>
        )}
      </div>

      <div className="relative my-5">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-3 text-gray-400 uppercase tracking-wider">
            {t('auth.orContinueWithEmail')}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* Honeypot field: hidden from sighted and screen-reader users, bots often fill every input. */}
        <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            type="text"
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={updateField('website')}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">{t('auth.firstName')}</label>
            <input type="text" value={form.firstName} onChange={updateField('firstName')} className={inputClass('firstName')} />
            {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
          </div>
          <div>
            <label className="label">{t('auth.lastName')}</label>
            <input type="text" value={form.lastName} onChange={updateField('lastName')} className={inputClass('lastName')} />
            {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
          </div>
        </div>

        <div>
          <label className="label">{t('auth.companyName')}</label>
          <input
            type="text"
            value={form.companyName}
            onChange={updateField('companyName')}
            className="input"
            required={!invitation}
            disabled={!!invitation}
            placeholder={invitation ? invitation.organization.name : ''}
          />
        </div>

        <div>
          <label className="label">{t('auth.email')}</label>
          <input
            type="email"
            value={form.email}
            onChange={updateField('email')}
            className={inputClass('email')}
            disabled={!!invitation}
          />
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </div>

        <div>
          <label className="label">{t('auth.password')}</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={updateField('password')}
              className={`${inputClass('password')} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">{t('auth.passwordComplexity')}</p>
          {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
        </div>

        <div>
          <label className="label">{t('auth.confirmPassword')}</label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={updateField('confirmPassword')}
              className={`${inputClass('confirmPassword')} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
            >
              {showConfirmPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
        </div>

        <button type="submit" disabled={register.isPending || googleLogin.isPending} className="btn-primary w-full flex items-center justify-center gap-2">
          {register.isPending && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
          )}
          {register.isPending ? t('auth.creatingAccount') : t('auth.register')}
        </button>
      </form>
    </div>
  );
}
